// src/components/Acetileno/AcetilenoList.tsx
import React, {
  useState,
  useEffect,
  useRef,
  ElementRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import DxDataGrid, {
  Column,
  Paging,
  Pager,
  ColumnFixing,
  FilterRow,
  HeaderFilter,
  Lookup,
  SearchPanel,
} from "devextreme-react/data-grid";
import { Button as DxButton } from "devextreme-react/button";
import { LoadIndicator } from "devextreme-react";
import notify from "devextreme/ui/notify";
import ExcelJS from "exceljs";
import saveAs from "file-saver";

import { Acetylene } from "../../dtos/Acetylene";
import { acetilenoService } from "../../services/acetilenoService";
import {
  GridContainer,
  HeaderContainer,
  PageContainer,
  Title,
  AddUserButton,
  DevExtremeModalGlobalStyles,
  FilterBar,
  Field,
  Label,
  StyledSelect,
  StyledInput,
  DateInput,
  ActionsRow,
} from "./Acetileno.styles";

import defaultHeaderImage from "../../assets/img/infra-logo.svg";
import { catalogoService } from "../../services/catalogoService";

// ====== Códigos de estatus (back) ======
const STATUS_PENDIENTE = 0;
const STATUS_CONSOLIDADO = 1;
const STATUS_REPORTADO = 4; // algunos servicios también usan 2 para "Reportado"
const STATUS_REPORTADO_ALT = 2; // cubrir ambos
const STATUS_DESCARTADO = 3;

// ===== helpers de fecha (YYYY-MM-DD) =====
const toYMD = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const todayYMD = () => toYMD(new Date());
const subDaysYMD = (days: number) =>
  toYMD(new Date(Date.now() - days * 24 * 60 * 60 * 1000));

const RegistrosList: React.FC = () => {
  const navigate = useNavigate();
  const dataGridRef = useRef<ElementRef<typeof DxDataGrid>>(null);

  const [acetylene, setAcetylene] = useState<Acetylene[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // ------- estado filtros / componentes -------
  const [sucursales, setSucursales] = useState<any[]>([]);
  const [filterSucursal, setFilterSucursal] = useState<number | "">("");
  const [filterOperador, setFilterOperador] = useState<string>("");
  // FECHAS POR DEFECTO
  const [filterStart, setFilterStart] = useState<string>(subDaysYMD(7));
  const [filterEnd, setFilterEnd] = useState<string>(todayYMD());

  // ====== cargar catálogo de sucursales para el <select> ======
  useEffect(() => {
    const loadBranches = async () => {
      try {
        const resp = await catalogoService.getBranch();
        setSucursales(resp?.data || []);
      } catch (err: any) {
        notify(
          { message: err?.message || "Error al cargar sucursales" },
          "error",
          3000
        );
      }
    };
    loadBranches();
  }, []);

  // ====== Carga inicial ======
  useEffect(() => {
    const initialSearch = async () => {
      const stored = localStorage.getItem("userData");
      setLoading(true);
      setError(null);
      try {
        if (!stored) return;
        const parsed = JSON.parse(stored);
        const sucursalId = parsed?.user?.sucursalId ?? 0;

        setFilterSucursal(sucursalId);

        const resp = await acetilenoService.getAcetilenoConsolidadoByFilters({
          sucursalId,
          fechainicio: filterStart,
          fechaFin: filterEnd,
          usuario: "",
        });

        if (resp?.error) {
          const msg = String(resp.error.message || "").toLowerCase();
          if (msg.includes("no se encontraron registros")) {
            setAcetylene([]);
            setError(null);
          } else {
            throw new Error(resp.error.message);
          }
        } else {
          setAcetylene((resp?.data || []) as Acetylene[]);
        }
      } catch (e: any) {
        setError(e.message || "Error desconocido al cargar datos");
        setAcetylene([]);
      } finally {
        setLoading(false);
      }
    };
    initialSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Solo mostrar REGISTROS REPORTADOS (2 ó 4) ----
  // ESTE arreglo es el *dataset filtrado final* que se ve en la tabla
  // y ES el que vamos a exportar COMPLETO al Excel, y ES el que vamos a sumar.
  const dataForGrid = useMemo(
    () =>
      acetylene.filter((item) =>
        [STATUS_REPORTADO, STATUS_REPORTADO_ALT].includes(
          (item.statusAcetileno as number) ?? -1
        )
      ),
    [acetylene]
  );

  // --- cuando cambia el dataset (o tras buscar), regresa a la página 0
  useEffect(() => {
    const instance = dataGridRef.current?.instance();
    if (instance) instance.pageIndex(0);
  }, [dataForGrid]);

  const fmt = (d?: string) => (d && d.length >= 10 ? d.slice(0, 10) : d || "");

  // ---- Buscar con filtros ----
  const handleSearch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const resp = await acetilenoService.getAcetilenoConsolidadoByFilters({
        sucursalId: filterSucursal === "" ? 0 : Number(filterSucursal),
        fechainicio: fmt(filterStart),
        fechaFin: fmt(filterEnd),
        usuario: filterOperador?.trim() || "",
      });

      if (resp?.error) {
        const msg = String(resp.error.message || "").toLowerCase();
        if (msg.includes("no se encontraron registros")) {
          setAcetylene([]);
          notify({
            message: "Sin resultados con los filtros aplicados.",
            type: "info",
            displayTime: 2500,
          });
        } else {
          throw new Error(resp.error.message);
        }
      } else {
        setAcetylene((resp?.data || []) as Acetylene[]);
      }
    } catch (e: any) {
      setError(e.message);
      setAcetylene([]);
    } finally {
      setLoading(false);
      // asegura reiniciar a la primera página tras una búsqueda
      setTimeout(() => dataGridRef.current?.instance()?.pageIndex(0), 0);
    }
  }, [filterSucursal, filterStart, filterEnd, filterOperador]);

  // ==== Textos por estatus (2 y 4 => Reportado) ====
  const getStatusText = (status: number | undefined) => {
    switch (status) {
      case STATUS_PENDIENTE:
        return "Pendiente";
      case STATUS_CONSOLIDADO:
        return "Consolidado";
      case STATUS_REPORTADO_ALT: // 2
      case STATUS_REPORTADO: // 4
        return "Reportado";
      case STATUS_DESCARTADO:
        return "Descartado";
      default:
        return "Desconocido";
    }
  };

  const getDateTimeString = (fecha: string, hora?: string) => {
    if (!fecha) return "";

    // 1. Nos quedamos solo con la parte de fecha (antes de la "T" si viene en ISO)
    const soloFecha = fecha.split("T")[0]; // "2025-10-28"

    // 2. Parseamos manualmente YYYY-MM-DD
    const [yyyy, mm, dd] = soloFecha.split("-");

    // 3. Formato deseado DD/MM/YYYY
    const fechaFormateada =
      dd && mm && yyyy ? `${dd}/${mm}/${yyyy}` : soloFecha;

    // 4. Hora, sin tocarla
    const horaFinal = hora && hora.trim() !== "" ? hora : "00:00:00";

    return `${fechaFormateada} ${horaFinal}`;
  };

  const loadLogoAsPngBase64 = async (url: string, w: number, h: number) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    return new Promise<string>((resolve, reject) => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Canvas no soportado"));
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/png").split(",")[1]);
      };
      img.onerror = () => reject(new Error("No se pudo cargar la imagen"));
      img.src = url;
    });
  };

  // === Exportar Excel con TODOS los registros filtrados (dataForGrid), NO solo la página visible ===
  const handleExportExcel = async () => {
    try {
      // usamos directamente TODO el dataset filtrado que estamos mostrando:
      const dataToExport = dataForGrid;

      if (dataToExport.length === 0) {
        notify({
          message: "No hay datos para exportar con los filtros actuales.",
          type: "warning",
          displayTime: 3500,
        });
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Acetileno");

      // Logo
      const imgBase64 = await loadLogoAsPngBase64(defaultHeaderImage, 150, 32);
      const logoId = workbook.addImage({ base64: imgBase64, extension: "png" });
      worksheet.addImage(logoId, "A2:B3");

      // Detectar sucursal única opcionalmente
      let sucursalTitle: string = "Varias Sucursales";
      const firstSucursal = dataToExport[0].sucursal;
      let nameSucursal: string = "N/A";
      const firstNumSucursal = dataToExport[0].nombreSucursal;

      if (
        firstSucursal &&
        dataToExport.every(
          (item) => String(item.sucursal) === String(firstSucursal)
        )
      ) {
        sucursalTitle = String(firstSucursal);
      }
      if (
        firstNumSucursal &&
        dataToExport.every(
          (item) => String(item.nombreSucursal) === String(firstNumSucursal)
        )
      ) {
        nameSucursal = String(firstNumSucursal);
      }

      // Título principal
      worksheet.mergeCells("A4:J4");
      const mainTitleCell = worksheet.getCell("A4");
      mainTitleCell.value = `Planta Acetileno ${sucursalTitle} - ${nameSucursal}`;
      mainTitleCell.font = { name: "Calibri", size: 14, bold: true };
      mainTitleCell.alignment = { horizontal: "center", vertical: "middle" };

      // Subtítulo
      worksheet.mergeCells("A6:J6");
      const subTitleCell = worksheet.getCell("A6");
      subTitleCell.value = "Reporte de Acetonado de Cilindros de Acetileno";
      subTitleCell.font = { name: "Calibri", size: 12, bold: true };
      subTitleCell.alignment = { horizontal: "center", vertical: "middle" };

      // Columnas en Excel
      worksheet.columns = [
        { key: "index", width: 5 },
        { key: "sucursal", width: 20 },
        { key: "nombreUsuario", width: 30 },
        { key: "fechaHora", width: 22 },
        { key: "celda", width: 10 },
        { key: "tipoEnvase" },
        { key: "cantidad", width: 20, style: { numFmt: "#,##0.00" } },
        { key: "statusAcetileno", width: 15 },
        { key: "consolidado", width: 15 },
      ];

      // Header row
      const headerRow = worksheet.getRow(8);
      headerRow.values = [
        "#",
        "Sucursal",
        "Operador",
        "Fecha y Hora",
        "Báscula",
        "Tipo Envase",
        "Cantidad",
        "Status",
        "Consolidado",
      ];
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF043F79" },
        };
        cell.font = {
          name: "Calibri",
          bold: true,
          color: { argb: "FFFFFFFF" },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Filas de datos (TODAS)
      const rows = dataToExport.map((item, index) => ({
        index: index + 1,
        sucursal: item.sucursal ?? "",
        nombreUsuario: item.nombreUsuario ?? "",
        fechaHora: getDateTimeString(
          item.fecha?.toString() || "",
          item.hora?.toString()
        ),
        celda: item.celda ?? "",
        tipoEnvase: item.tipoEnvase ?? "",
        cantidad: item.cantidad ?? 0,
        statusAcetileno: getStatusText(item.statusAcetileno),
        consolidado: item.consolidado ? "Sí" : "No",
      }));
      worksheet.addRows(rows);

      // Ajuste dinámico ancho columna "Tipo Envase"
      const tipoEnvaseColumn = worksheet.getColumn("F");
      let maxLength = "Tipo Envase".length;
      dataToExport.forEach((item) => {
        const length = item.tipoEnvase ? item.tipoEnvase.length : 0;
        if (length > maxLength) maxLength = length;
      });
      tipoEnvaseColumn.width = maxLength + 2;

      // Total de acetona (suma cantidad) en TODO el dataset filtrado
      const dataStartRow = 9;
      const totalAcetona = dataToExport.reduce(
        (sum, item) => sum + (item.cantidad ?? 0),
        0
      );
      const totalRowNumber = dataStartRow + dataToExport.length;
      worksheet.mergeCells(`A${totalRowNumber}:F${totalRowNumber}`);
      const labelCell = worksheet.getRow(totalRowNumber).getCell("A");
      labelCell.value = "Total Acetona:";
      labelCell.alignment = { horizontal: "right" };
      labelCell.font = { name: "Calibri", bold: true };

      const valueCell = worksheet.getRow(totalRowNumber).getCell("G");
      valueCell.value = totalAcetona;
      valueCell.font = { name: "Calibri", bold: false };
      valueCell.numFmt = "#,##0.00";

      // Descargar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), "Reporte_Acetonado.xlsx");
    } catch (error: any) {
      console.error("Ocurrió un error al exportar a Excel:", error);
      notify({ message: `Error al exportar: ${error.message}`, type: "error" });
    }
  };

  const onEditorPreparing = (e: any) => {
    if (e.dataField === "nomina" && e.parentType === "dataRow") {
      e.editorOptions.disabled = e.row?.data?.is365 === true;
    }
  };

  // ==== Render de estatus basado en código (colores correctos) ====
  const statusCellRender = (cellData: any) => {
    const s = cellData.value as number | undefined;
    let text = "Desconocido";
    let color = "#172B4D";

    if (s === STATUS_PENDIENTE) {
      text = "Pendiente";
      color = "#F8981D";
    } else if (s === STATUS_CONSOLIDADO) {
      text = "Consolidado";
      color = "#172B4D";
    } else if (s === STATUS_REPORTADO || s === STATUS_REPORTADO_ALT) {
      text = "Reportado";
      color = "#0052CC";
    } else if (s === STATUS_DESCARTADO) {
      text = "Descartado";
      color = "#6B778C";
    }

    return <span style={{ color, fontWeight: "bold" }}>{text}</span>;
  };

  // === Total global de acetona (sobre TODO el dataset filtrado lógico, sin importar paginación del grid) ===
  const totalAcetonaGlobal = useMemo(() => {
    return dataForGrid.reduce((sum, row) => {
      const val = Number(row.cantidad ?? 0);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
  }, [dataForGrid]);

  if (loading) {
    return (
      <PageContainer
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadIndicator height={60} width={60} visible />
      </PageContainer>
    );
  }

  return (
    <>
      <DevExtremeModalGlobalStyles />
      <PageContainer>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <DxButton
            icon="chevronleft"
            text="Regresar"
            stylingMode="outlined"
            style={{ borderColor: "blue", color: "blue" }}
            onClick={() => navigate(-1)}
          />
        </div>

        <HeaderContainer>
          <Title>Reporte histórico</Title>

          <ActionsRow>
            {/* Vista fija: Reportados */}
            <span
              style={{
                fontWeight: 600,
                padding: "8px 12px",
                border: "1px solid #DFE1E6",
                borderRadius: 6,
                background: "#F1F5FF",
              }}
            >
              Vista: Reportados
            </span>

            <div ref={menuRef} style={{ position: "relative", marginLeft: 8 }}>
              <AddUserButton onClick={() => setShowExportMenu((p) => !p)}>
                Exportar ▼
              </AddUserButton>
              {showExportMenu && (
                <div
                  style={{
                    position: "absolute",
                    backgroundColor: "#fff",
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
                    zIndex: 100,
                    right: 0,
                    borderRadius: "4px",
                    overflow: "hidden",
                    border: "1px solid #DFE1E6",
                  }}
                >
                  <div
                    onClick={() => {
                      handleExportExcel();
                      setShowExportMenu(false);
                    }}
                    style={{
                      padding: "10px 18px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f8f9fa")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    📄 Exportar a Excel
                  </div>
                </div>
              )}
            </div>
          </ActionsRow>
        </HeaderContainer>

        <FilterBar>
          <Field>
            <Label>Sucursal</Label>
            <StyledSelect
              value={filterSucursal}
              onChange={(e) =>
                setFilterSucursal(
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
            >
              <option value="">Todas</option>
              {sucursales.map((o) => {
                const id =
                  o.id ?? o.sucursalId ?? o.value ?? o.cc ?? o.numero ?? "";
                const num = o.numero ?? o.cc ?? o.sucursal ?? "";
                const name = o.nombreSuc ?? o.nombre ?? o.name ?? "";
                const label =
                  [num, name].filter(Boolean).join(" - ") || String(id);
                return (
                  <option key={id} value={id}>
                    {label}
                  </option>
                );
              })}
            </StyledSelect>
          </Field>

          <Field>
            <Label>Operador</Label>
            <StyledInput
              placeholder="Ej. jperez"
              value={filterOperador}
              onChange={(e) => setFilterOperador(e.target.value)}
            />
          </Field>

          <Field>
            <Label>Fecha inicio</Label>
            <DateInput
              type="date"
              value={filterStart}
              onChange={(e) => setFilterStart(e.target.value)}
              max={filterEnd || undefined}
            />
          </Field>

          <Field>
            <Label>Fecha fin</Label>
            <DateInput
              type="date"
              value={filterEnd}
              onChange={(e) => setFilterEnd(e.target.value)}
              min={filterStart || undefined}
            />
          </Field>

          <AddUserButton onClick={handleSearch}>Buscar</AddUserButton>
        </FilterBar>

        <GridContainer>
          <DxDataGrid
            ref={dataGridRef}
            dataSource={dataForGrid}
            keyExpr="id"
            onEditorPreparing={onEditorPreparing}
            wordWrapEnabled
            showBorders
            remoteOperations={false}
            columnAutoWidth
            columnHidingEnabled={false}
          >
            <SearchPanel
              visible
              highlightCaseSensitive
              placeholder="Buscar..."
            />

            <Column
              caption="#"
              width={60}
              alignment="center"
              allowSorting={false}
              allowFiltering={false}
              cellRender={(e) => {
                const pageIndex = e.component.pageIndex();
                const pageSize = e.component.pageSize();
                const absoluteIndex = pageIndex * pageSize + e.rowIndex + 1;
                return <span>{absoluteIndex}</span>;
              }}
            />

            <Column
              dataField="ccSucursal"
              caption="CC - Sucursal"
              calculateDisplayValue={(row: Acetylene) => {
                const nom = row?.nombreSucursal ?? "";
                const num = row?.sucursal || row.numeroSucursal;
                if (!nom && !num) return "";
                if (!nom) return String(num);
                if (!num) return nom;
                return `${num} - ${nom}`;
              }}
              allowFiltering
            />

            <Column
              dataField="nombreUsuario"
              caption="Operador"
              allowFiltering
            />

            <Column
              caption="Fecha y Hora"
              dataField="fechaCompleta"
              dataType="datetime"
              allowFiltering
              format="dd/MM/yyyy HH:mm:ss"
              calculateCellValue={(data: any) => {
                if (!data?.fecha) return null;
                const iso = `${data.fecha}T${data.hora || "00:00:00"}`;
                const d = new Date(iso);
                return isNaN(d.getTime()) ? null : d;
              }}
            />

            <Column dataField="celda" caption="Báscula" allowFiltering />
            <Column
              dataField="tipoEnvase"
              caption="Tipo envase"
              allowFiltering
            />
            <Column
              dataField="cantidad"
              caption="Cantidad acetona"
              dataType="number"
              width={150}
              alignment="left"
              allowFiltering
            />

            <Column
              dataField="statusAcetileno"
              caption="Estatus"
              allowFiltering
              cellRender={statusCellRender}
            >
              <Lookup
                dataSource={[
                  { value: STATUS_REPORTADO, text: "Reportado" },
                  { value: STATUS_REPORTADO_ALT, text: "Reportado" },
                ]}
                valueExpr="value"
                displayExpr="text"
              />
            </Column>

            <FilterRow visible />
            <HeaderFilter visible />

            {/* Paginación consistente */}
            <Paging defaultPageSize={50} />
            <Pager
              visible
              allowedPageSizes={[50]}
              showPageSizeSelector
              showInfo
              showNavigationButtons
            />

            <ColumnFixing enabled />

            {/* ⛔ IMPORTANTE:
                Quitamos <Summary> porque solo suma la página actual
                y eso confunde.
            */}
          </DxDataGrid>

          {/* Footer con el total GLOBAL sin importar la página */}
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              fontWeight: 600,
              fontSize: 14,
              padding: "8px 12px",
              borderTop: "1px solid #DFE1E6",
              backgroundColor: "#F7F8FA",
            }}
          >
            <div style={{ minWidth: 200, textAlign: "right" }}>
              Total acetona (todas las filas):{" "}
              {totalAcetonaGlobal.toLocaleString("es-MX", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
        </GridContainer>
      </PageContainer>
    </>
  );
};

export default RegistrosList;
